import React, { useState, useEffect, ReactNode, createContext } from "react";
import { Modal } from "react-bootstrap";
import BaseButton from "./BaseButton";
import "./css/ModalButton.css";

type TitlePosition = "left" | "right";

interface ModalButtonProps {
    IconComponent: ReactNode;
    Title?: string;
    titlePosition?: TitlePosition;
    modalTitle?: string;
    children: ReactNode;
    className?: string;
    externalCloseSignal?: number;
    onOpenRequest?: () => void;
    onOpenComplete?: () => void;
    onCloseRequest?: () => void;
}

export const ModalCloseContext = createContext<(() => void) | null>(null);

export const ModalButton: React.FC<ModalButtonProps> = ({
    IconComponent,
    Title,
    titlePosition = "right",
    modalTitle,
    children,
    className = "",
    externalCloseSignal,
    onOpenRequest,
    onOpenComplete,
    onCloseRequest
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const openModal = () => {
        onOpenRequest?.();
        setIsOpen(true);
    };

    const closeModal = (goBack?: boolean | undefined) => {
        onCloseRequest?.();
        setIsOpen(false);
        if (goBack) {
            window.history.back();
        }
    };

    // Fire onOpenComplete after modal animation finishes
    useEffect(() => {
        if (isOpen && onOpenComplete) {
            const timer = setTimeout(() => {
                onOpenComplete();
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [isOpen, onOpenComplete]);

    // Back-button closure support
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handlePop = () => {
            closeModal();
        };

        window.addEventListener("popstate", handlePop);
        window.history.pushState({ modal: true }, "");

        return () => {
            window.removeEventListener("popstate", handlePop);
        };
    }, [isOpen]);

    useEffect(() => {
        if (externalCloseSignal !== undefined) {
            closeModal();
        }
    }, [externalCloseSignal]);

    const renderButtonContent = () => {
        if (!Title) {
            return (
                <div className="modal-button-icon-only">
                    {IconComponent}
                </div>
            );
        }

        const titleEl = <span className="modal-button-title">{Title}</span>;

        return (
            <div className={`modal-button-with-title`}>
                {titlePosition === "left" ? (
                    <>
                        {titleEl}
                        {IconComponent}
                    </>
                ) : (
                    <>
                        {IconComponent}
                        {titleEl}
                    </>
                )}
            </div>
        );
    };

    return (
        <>
            <BaseButton
                ariaLabel={Title || "Open modal"}
                className={`modal-button settle-effect ${isOpen === true ? ' active' : ''} ${className}`}
                onClick={openModal}>
                {renderButtonContent()}
            </BaseButton>

            <Modal
                show={isOpen}
                onHide={() => { closeModal(true); }}
                className={`${className}`}
                backdrop
                keyboard
                centered>

                <Modal.Header closeButton>
                    {modalTitle && (
                        <Modal.Title>{modalTitle}</Modal.Title>
                    )}
                </Modal.Header>

                <ModalCloseContext.Provider value={closeModal}>
                    <Modal.Body>
                        {children}
                    </Modal.Body>
                </ModalCloseContext.Provider>
            </Modal>
        </>
    );
};

export default ModalButton;
